// FT-191: чистые функции блокировки общего компьютера. Без Vue и стора — main.js не
// импортируется ни одним спеком, поэтому решения живут здесь.
import { STAFF_ROLES } from "@/constants/roles";
import Cookies from "js-cookie";
import { emitDeviceEvent, freezePersistence, isPersistenceFrozen, DEVICE_EVENT_KEY } from "@/utils/deviceTabs";

export const DEVICE_SECRET_KEY = "rp_trusted_device";
export const LAST_ACTIVITY_KEY = "rp_last_activity";
// Таймаут простоя — общий для всех вкладок: сохранённая настройка доходит до уже открытых вкладок
// без перезагрузки (блокировка одна на все, а app_version каждая читает только при загрузке).
export const IDLE_MINUTES_KEY = "rp_idle_minutes";
export const PENDING_LOGIN_KEY = "rp_device_pending_login";
export const PIN_FORMAT = /^\d{4}$/; // ровно 4 цифры (решение USER 2026-10-09)
export const DEFAULT_IDLE_MINUTES = 5;

export function readDeviceSecret(storage = window.localStorage) {
  try {
    return storage.getItem(DEVICE_SECRET_KEY) || null;
  } catch (e) {
    return null;
  }
}

export function writeDeviceSecret(secret, storage = window.localStorage) {
  try {
    storage.setItem(DEVICE_SECRET_KEY, secret);
  } catch (e) {
    // приватный режим или переполненное хранилище — компьютер не станет общим
  }
}

export function clearDeviceSecret(storage = window.localStorage) {
  try {
    storage.removeItem(DEVICE_SECRET_KEY);
  } catch (e) {
    // нечего чистить
  }
}

export function readLastActivity(storage = window.localStorage) {
  try {
    const value = Number(storage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch (e) {
    return null;
  }
}

// Любой успешный вход и разблокировка: иначе просроченная метка, из-за которой экран
// заблокировался, тут же заблокирует его снова.
export function markActivity(storage = window.localStorage, now = Date.now()) {
  try {
    storage.setItem(LAST_ACTIVITY_KEY, String(now));
  } catch (e) {
    // без хранилища метка живёт только в трекере вкладки
  }
}

export function idleSeconds(now, lastActivityMs) {
  return Math.max(0, Math.floor((now - (lastActivityMs == null ? now : lastActivityMs)) / 1000));
}

// Нет метки — истёк: после сна или перезапуска браузера экран блокируется сразу.
export function isIdleExpired(now, lastActivityMs, idleMinutes) {
  if (lastActivityMs == null) return true;
  return now - lastActivityMs >= idleMinutes * 60_000;
}

// Отдельный пинг — только если человек активен, а запросов с X-Device-Idle не было минуту
// (долгое заполнение формы). Ушёл — не продлеваем: сервер сам завершит сессию.
export function needsKeepalivePing(now, lastActivityMs, lastHeaderSentMs) {
  if (lastActivityMs == null || now - lastActivityMs >= 60_000) return false;
  return lastHeaderSentMs == null || now - lastHeaderSentMs >= 60_000;
}

// REQ-06: на зарегистрированном компьютере сессия сотрудника своей MainCompany не в режиме
// устройства (вход до регистрации или до включения функции) завершается.
export function shouldDropNonDeviceSession({ device, loggedIn, deviceMode, role, mainCompanyId }) {
  if (!device || !loggedIn || deviceMode) return false;
  if (STAFF_ROLES.indexOf(role) === -1) return false;
  return Number(mainCompanyId) === Number(device.main_company_id);
}

// Единственный путь «закончить всё и на полный вход» на общем компьютере: «Другой сотрудник»,
// потеря устройства, pin_disabled. Полная перезагрузка — роутер с его запретом переходов при
// блокировке не участвует, persisted-блокировка её не переживает. Секрет устройства остаётся.
export function resetToSignin({ storage = window.localStorage, location = window.location } = {}) {
  freezePersistence();
  emitDeviceEvent("switch", {}, storage);
  purgeUserStorage(storage);
  location.replace("/signin");
}

// REQ-09: при смене сотрудника с общего компьютера уходит всё пользовательское — стор, токен,
// запомненный логин, история страниц, пресеты фильтров, чужие ключи (чат и т. п.) и cookie с
// токеном. Остаются только признаки устройства и нейтральные настройки интерфейса.
const KEEP_KEYS = [ DEVICE_SECRET_KEY, LAST_ACTIVITY_KEY, IDLE_MINUTES_KEY, DEVICE_EVENT_KEY, "client_token", "lang", "ganttLeftPanelWidth" ];

export function purgeUserStorage(storage = window.localStorage, { removeCookie = (name) => Cookies.remove(name) } = {}) {
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) keys.push(storage.key(i));
    keys.filter((key) => key && KEEP_KEYS.indexOf(key) === -1).forEach((key) => storage.removeItem(key));
  } catch (e) {
    // хранилище недоступно — перезагрузка всё равно уведёт на вход
  }
  removeCookie("auth_token");
}

// Метку активности ставит только живая вкладка в режиме устройства: заблокированная, закрытая
// сменой сотрудника или замороженная не продлевает чужую сессию движением мыши.
export function shouldCountActivity({ deviceMode, locked, tabClosed, frozen }) {
  return !!deviceMode && !locked && !tabClosed && !frozen;
}

export function activityCountable(state) {
  return shouldCountActivity({ ...state, frozen: isPersistenceFrozen() });
}

// До монтирования: сессия в режиме устройства, а человек отсутствовал дольше таймаута.
export function shouldBootLock(state, now, lastActivityMs) {
  if (!state || !state.deviceMode || state.locked) return false;
  return isIdleExpired(now, lastActivityMs, currentIdleMinutes(state.device));
}

// Роутер: пока экран заблокирован или вкладку закрыла смена сотрудника, переходов нет — пустой
// токен иначе увёл бы на форму входа и размонтировал страницу под экраном блокировки (ASM-01).
// Легитимные выходы с экрана блокировки идут через resetToSignin, мимо роутера.
export function navigationBlocked({ locked, tabClosed }) {
  return !!locked || !!tabClosed;
}

// REQ-09: разблокировка другим сотрудником. Вкладка замораживает запись хранилища, закрывает
// остальные вкладки, кладёт ответ входа в свой sessionStorage, чистит состояние прежнего
// сотрудника и перезагружается на форму входа — Signin применит ответ штатным путём входа.
export function switchToAnotherUser(response, { storage = window.localStorage, session = window.sessionStorage, location = window.location } = {}) {
  try {
    session.setItem(PENDING_LOGIN_KEY, JSON.stringify({ data: response.data, headers: { authorization: response.headers.authorization } }));
  } catch (e) {
    // без sessionStorage новый сотрудник войдёт на форме входа сам
  }
  resetToSignin({ storage, location });
}

// Ответ входа, оставленный switchToAnotherUser, — один раз.
export function takePendingLogin(session = window.sessionStorage) {
  let raw = null;
  try {
    raw = session.getItem(PENDING_LOGIN_KEY);
    session.removeItem(PENDING_LOGIN_KEY);
  } catch (e) {
    return null;
  }
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch (e) {
    return null;
  }
}

// Ctrl+Shift+L / Cmd+Shift+L — заблокировать. По `code`, чтобы работало в русской раскладке.
export function isLockHotkey(event) {
  if (!event || !event.shiftKey || !(event.ctrlKey || event.metaKey)) return false;
  return event.code === "KeyL" || String(event.key || "").toLowerCase() === "l";
}

// Любой успешный вход (мутация setUserInfo — у неё шесть точек входа): режим общего компьютера по
// ответу сервера, снятая блокировка, свежая метка активности (иначе просроченная метка тут же
// заблокирует снова) и предложение задать PIN, если его ещё нет.
export function applyLoginToDeviceState(store, response) {
  const data = (response && response.data) || {};
  const trusted = data.trusted_device === true;
  store.commit("deviceLock/SET_DEVICE_MODE", trusted);
  store.commit("deviceLock/UNLOCK");
  markActivity();
  if (!trusted) return;
  // Сервер уже внёс сотрудника в список устройства — экран блокировки покажет его без перезагрузки.
  const user = data.user;
  if (user && user.id != null) store.commit("deviceLock/ADD_DEVICE_USER", { id: user.id, name: user.name, avatar_url: user.avatar_url || null });
  // Полный вход (не разблокировка) на общем компьютере — новая сессия устройства: остальные вкладки
  // со страницами прежнего сотрудника закрываются тем же протоколом, что и смена по PIN (REQ-09, FM-03).
  if (!response.deviceUnlock) announceNewDeviceSession();
  if (data.pin_set === false) store.commit("deviceLock/REQUEST_PIN_SETUP");
}

function announceNewDeviceSession() {
  emitDeviceEvent("switch");
  // Новый сотрудник уже в хранилище — закрытые вкладки могут перезагружаться. Отдельным тиком:
  // два события подряд в одном ключе не должны слиться.
  setTimeout(() => emitDeviceEvent("switch_ready"), 50);
}

// id сотрудника из JWT (claim sub) без проверки подписи — только чтобы не подставить вкладке токен
// другого сотрудника; null, если токен не разобрать.
export function tokenUserId(token) {
  try {
    const payload = String(token || "").replace(/^Bearer\s+/i, "").split(".")[1];
    if (!payload) return null;
    const json = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
    return json.sub != null ? String(json.sub) : null;
  } catch (e) {
    return null;
  }
}

export function deviceLockPlugin(store) {
  store.subscribe((mutation) => {
    if (mutation.type === "setUserInfo") applyLoginToDeviceState(store, mutation.payload);
  });
}

// Оверлей поверх смонтированной страницы (экран блокировки, закрытая вкладка): всё вне него —
// inert, иначе Tab и скринридер уходят в невидимую страницу прежнего сотрудника. Помечаются
// соседи на каждом уровне от оверлея до body (диалоги Vuetify лежат там же), кроме другого оверлея
// устройства (data-device-overlay); возвращает отмену, снимающую только свои пометки.
export function isolateOverlay(el) {
  const marked = [];
  for (let node = el; node && node.parentElement && node !== document.body; node = node.parentElement) {
    Array.from(node.parentElement.children).forEach((sibling) => {
      if (sibling === node || sibling.hasAttribute("inert") || sibling.hasAttribute("data-device-overlay")) return;
      sibling.setAttribute("inert", "");
      marked.push(sibling);
    });
  }
  return () => marked.forEach((sibling) => sibling.removeAttribute("inert"));
}

// Блок device из ответа app_version (CTR-02). undefined — сервер не смог ответить про устройство
// (авария в app_version): состояние не трогаем, иначе временный сбой выкинул бы всех со смены (FM-04
// — только на явное «устройства нет», то есть null).
export function deviceFromAppVersion(data) {
  if (!data || data.device_unavailable) return undefined;
  return data.device || null;
}

// 401 на общем компьютере (CTR-05). ignore — вкладка уже закрыта сменой сотрудника; stale — в общем
// хранилище токен другого сотрудника (он вошёл в другой вкладке, а switch сюда ещё не дошёл): эта
// вкладка — страница прежнего, она закрывается сама, не трогая хранилище и не рассылая lock, иначе
// стёрла бы токен нового и заблокировала его; lock — сессия этого сотрудника мертва.
export function deviceAuthFailureAction({ tabClosed, storedToken, currentUserId }) {
  if (tabClosed) return "ignore";
  const owner = tokenUserId(storedToken);
  if (owner != null && currentUserId != null && owner !== String(currentUserId)) return "stale";
  return "lock";
}

// Вкладка открылась уже заблокированной (просроченная активность или блокировка пережила F5):
// страница под экраном грузилась без токена и пуста — после разблокировки её надо загрузить заново.
// Загруженную страницу разблокировка, наоборот, сохраняет (REQ-08).
let bootedLocked = false;
export function markBootedLocked(value = true) {
  bootedLocked = value;
}
export function reloadIfBootedLocked(location = window.location) {
  if (!bootedLocked) return false;
  location.reload();
  return true;
}

export function writeIdleMinutes(minutes, storage = window.localStorage) {
  try {
    if (Number.isInteger(minutes) && minutes > 0) storage.setItem(IDLE_MINUTES_KEY, String(minutes));
  } catch (e) {
    // хранилище недоступно — вкладка живёт значением из своего app_version
  }
}

// Общее значение из хранилища, иначе — из блока device этой вкладки, иначе — по умолчанию.
export function currentIdleMinutes(device, storage = window.localStorage) {
  let stored = null;
  try {
    stored = Number(storage.getItem(IDLE_MINUTES_KEY));
  } catch (e) {
    stored = null;
  }
  if (Number.isInteger(stored) && stored > 0) return stored;
  return (device && device.idle_minutes) || DEFAULT_IDLE_MINUTES;
}
